import Phaser from "phaser";
import { Cat } from "../sprites/CatSprite";
import {
  AssetLocator,
  DEFAULT_ANIMATION,
  type AccessoryType,
  type Animation,
} from "../sprites/AssetLocator";

interface AccessoryState {
  clothes: string | null;
  hat: string | null;
  glasses: string | null;
}

export class CatShowcaseScene extends Phaser.Scene {
  private cats: Cat[] = [];
  private accessoryState: AccessoryState = {
    clothes: null,
    hat: null,
    glasses: null,
  };
  private currentAnimation: Animation = DEFAULT_ANIMATION;

  private accessoryButtons: { [key: string]: Phaser.GameObjects.Text } = {};
  private animationButtons: { [key: string]: Phaser.GameObjects.Text } = {};

  constructor() {
    super({ key: "CatShowcaseScene" });
  }

  create() {
    console.log("CatShowcaseScene: Creating showcase of all 15 naked cats");

    // Set background
    this.cameras.main.setBackgroundColor(0x2c3e50);

    // Get screen dimensions
    const screenWidth = this.scale.width;
    const screenHeight = this.scale.height;

    // Calculate grid layout for 15 cats (5 columns x 3 rows works well)
    const cols = 5;
    const rows = 3;
    const cellWidth = screenWidth / cols;
    const cellHeight = (screenHeight - 300) / rows; // Leave more space for UI at top and bottom

    // Add title
    this.add
      .text(screenWidth / 2, 30, "Cat Showcase - All 15 Designs", {
        fontSize: "24px",
        color: "#ffffff",
        fontFamily: "Arial",
      })
      .setOrigin(0.5);

    // Create accessory toggle controls
    this.createAccessoryControls();

    // Create animation toggle controls
    this.createAnimationControls();

    // Create all 15 cats (all naked)
    for (let catNumber = 1; catNumber <= 15; catNumber++) {
      const index = catNumber - 1; // 0-based index for grid calculation
      const col = index % cols;
      const row = Math.floor(index / cols);

      // Calculate position (leave space for title and controls)
      const x = col * cellWidth + cellWidth / 2;
      const y = row * cellHeight + cellHeight / 2 + 250; // +250 for title and controls space

      try {
        // Create cat with NO accessories (naked)
        const catOptions = {
          catNumber,
          // No clothes, hat, or glasses - all cats start naked
        };

        const cat = new Cat(this, x, y, catOptions);

        // Scale cat to fit in cell (make them reasonably sized)
        const targetSize = Math.min(cellWidth, cellHeight - 40) * 0.6; // -40 for text space
        const catScale = targetSize / 200;
        cat.setScale(catScale);

        // Add cat to scene
        this.add.existing(cat);
        this.cats.push(cat);

        // Add label showing cat number
        this.add
          .text(x, y + targetSize / 2 + 20, `Cat ${catNumber}`, {
            fontSize: "14px",
            color: "#ffffff",
            fontFamily: "Arial",
          })
          .setOrigin(0.5);

        // Add click handler to log cat info
        cat.setInteractive();
      } catch (error) {
        console.error(`Failed to create cat ${catNumber}:`, error);

        // Add error placeholder
        const errorText = this.add
          .text(x, y, `Cat ${catNumber}\nError`, {
            fontSize: "14px",
            color: "#ff6b6b",
            fontFamily: "Arial",
            align: "center",
          })
          .setOrigin(0.5);
      }
    }

    // Add instructions
    this.add
      .text(
        screenWidth / 2,
        screenHeight - 50,
        "Use accessory & animation buttons to customize all cats • Try fly, hit, stunned, throwing for special effects!",
        {
          fontSize: "14px",
          color: "#bdc3c7",
          fontFamily: "Arial",
        },
      )
      .setOrigin(0.5);

    // Add current animation status (will be updated dynamically)
    const animationStatus = this.add
      .text(
        screenWidth - 20,
        20,
        `Current Animation: ${this.currentAnimation}`,
        {
          fontSize: "14px",
          color: "#f39c12",
          fontFamily: "Arial",
        },
      )
      .setOrigin(1, 0);

    // Store reference for updates
    (this as any).animationStatusText = animationStatus;

    // Add back button
    const backButton = this.add
      .text(50, 30, "← Back", {
        fontSize: "18px",
        color: "#3498db",
        fontFamily: "Arial",
      })
      .setOrigin(0, 0.5);

    backButton.setInteractive();
    backButton.on("pointerdown", () => {
      console.log("Going back to previous scene");
      this.scene.start("BudgetSceneReact");
    });

    backButton.on("pointerover", () => {
      backButton.setColor("#2980b9");
    });

    backButton.on("pointerout", () => {
      backButton.setColor("#3498db");
    });

    console.log(
      `CatShowcaseScene: Created ${this.cats.length} naked cats successfully`,
    );

    // Notify React overlay to hide during showcase
    window.dispatchEvent(
      new CustomEvent("phaser-overlay-toggle", { detail: { visible: false } }),
    );
  }

  private createAccessoryControls() {
    const screenWidth = this.scale.width;
    const startY = 70;
    const buttonHeight = 30;
    const spacing = 5;

    // Clothes section
    this.add.text(20, startY, "Clothes:", {
      fontSize: "16px",
      color: "#ffffff",
      fontFamily: "Arial",
      fontStyle: "bold",
    });

    const clothesOptions = [
      "None",
      ...AssetLocator.getAvailableAccessories("clothes"),
    ];
    clothesOptions.forEach((clothes, index) => {
      const button = this.add.text(
        100 + index * 120,
        startY,
        clothes.replace("_", " "),
        {
          fontSize: "14px",
          color: "#ffffff",
          fontFamily: "Arial",
          backgroundColor: "#34495e",
          padding: { x: 8, y: 4 },
        },
      );

      button.setInteractive();
      button.on("pointerdown", () =>
        this.toggleClothes(clothes === "None" ? null : clothes),
      );
      button.on("pointerover", () => button.setBackgroundColor("#2c3e50"));
      button.on("pointerout", () => button.setBackgroundColor("#34495e"));

      this.accessoryButtons[`clothes_${clothes}`] = button;
    });

    // Hats section
    this.add.text(20, startY + buttonHeight + spacing, "Hats:", {
      fontSize: "16px",
      color: "#ffffff",
      fontFamily: "Arial",
      fontStyle: "bold",
    });

    const hatOptions = ["None", ...AssetLocator.getAvailableAccessories("hat")];
    hatOptions.forEach((hat, index) => {
      const button = this.add.text(
        100 + index * 120,
        startY + buttonHeight + spacing,
        hat.replace("_", " "),
        {
          fontSize: "14px",
          color: "#ffffff",
          fontFamily: "Arial",
          backgroundColor: "#34495e",
          padding: { x: 8, y: 4 },
        },
      );

      button.setInteractive();
      button.on("pointerdown", () =>
        this.toggleHat(hat === "None" ? null : hat),
      );
      button.on("pointerover", () => button.setBackgroundColor("#2c3e50"));
      button.on("pointerout", () => button.setBackgroundColor("#34495e"));

      this.accessoryButtons[`hat_${hat}`] = button;
    });

    // Glasses section
    this.add.text(20, startY + (buttonHeight + spacing) * 2, "Glasses:", {
      fontSize: "16px",
      color: "#ffffff",
      fontFamily: "Arial",
      fontStyle: "bold",
    });

    const glassesOptions = [
      "None",
      ...AssetLocator.getAvailableAccessories("glasses"),
    ];
    glassesOptions.forEach((glasses, index) => {
      const button = this.add.text(
        100 + index * 120,
        startY + (buttonHeight + spacing) * 2,
        glasses.replace("_", " "),
        {
          fontSize: "14px",
          color: "#ffffff",
          fontFamily: "Arial",
          backgroundColor: "#34495e",
          padding: { x: 8, y: 4 },
        },
      );

      button.setInteractive();
      button.on("pointerdown", () =>
        this.toggleGlasses(glasses === "None" ? null : glasses),
      );
      button.on("pointerover", () => button.setBackgroundColor("#2c3e50"));
      button.on("pointerout", () => button.setBackgroundColor("#34495e"));

      this.accessoryButtons[`glasses_${glasses}`] = button;
    });

    // Update button states
    this.updateButtonStates();
  }

  private toggleClothes(clothes: string | null) {
    // Update state
    this.accessoryState.clothes = clothes;

    // Apply to all cats
    this.cats.forEach((cat) => {
      if (clothes) {
        cat.equip("clothes", clothes);
      } else {
        cat.unequip("clothes");
      }
      // No need to call animate() - accessories will automatically use current animation
    });

    this.updateButtonStates();
  }

  private toggleHat(hat: string | null) {
    // Update state
    this.accessoryState.hat = hat;

    // Apply to all cats
    this.cats.forEach((cat) => {
      if (hat) {
        cat.equip("hat", hat);
      } else {
        cat.unequip("hat");
      }
      // No need to call animate() - accessories will automatically use current animation
    });

    this.updateButtonStates();
  }

  private toggleGlasses(glasses: string | null) {
    // Update state
    this.accessoryState.glasses = glasses;

    // Apply to all cats
    this.cats.forEach((cat) => {
      if (glasses) {
        cat.equip("glasses", glasses);
      } else {
        cat.unequip("glasses");
      }
      // No need to call animate() - accessories will automatically use current animation
    });

    this.updateButtonStates();
  }

  private updateButtonStates() {
    // Update clothes buttons
    const clothesOptions = [
      "None",
      ...AssetLocator.getAvailableAccessories("clothes"),
    ];
    clothesOptions.forEach((clothes) => {
      const button = this.accessoryButtons[`clothes_${clothes}`];
      if (button) {
        const isActive =
          (clothes === "None" && !this.accessoryState.clothes) ||
          clothes === this.accessoryState.clothes;
        button.setBackgroundColor(isActive ? "#e74c3c" : "#34495e");
      }
    });

    // Update hat buttons
    const hatOptions = ["None", ...AssetLocator.getAvailableAccessories("hat")];
    hatOptions.forEach((hat) => {
      const button = this.accessoryButtons[`hat_${hat}`];
      if (button) {
        const isActive =
          (hat === "None" && !this.accessoryState.hat) ||
          hat === this.accessoryState.hat;
        button.setBackgroundColor(isActive ? "#e74c3c" : "#34495e");
      }
    });

    // Update glasses buttons
    const glassesOptions = [
      "None",
      ...AssetLocator.getAvailableAccessories("glasses"),
    ];
    glassesOptions.forEach((glasses) => {
      const button = this.accessoryButtons[`glasses_${glasses}`];
      if (button) {
        const isActive =
          (glasses === "None" && !this.accessoryState.glasses) ||
          glasses === this.accessoryState.glasses;
        button.setBackgroundColor(isActive ? "#e74c3c" : "#34495e");
      }
    });
  }

  private createAnimationControls() {
    const screenWidth = this.scale.width;
    const startY = 170; // Position below accessory controls
    const buttonWidth = 80;
    const buttonHeight = 25;
    const spacing = 5;

    // Animation section title
    this.add.text(20, startY, "Animations:", {
      fontSize: "16px",
      color: "#ffffff",
      fontFamily: "Arial",
      fontStyle: "bold",
    });

    // Get all available animations
    const animations = AssetLocator.getAvailableAnimations();

    // Create animation buttons in rows
    const buttonsPerRow = Math.floor(
      (screenWidth - 40) / (buttonWidth + spacing),
    );

    animations.forEach((animation, index) => {
      const row = Math.floor(index / buttonsPerRow);
      const col = index % buttonsPerRow;

      const x = 20 + col * (buttonWidth + spacing);
      const y = startY + 25 + row * (buttonHeight + spacing);

      const button = this.add.text(x, y, animation, {
        fontSize: "12px",
        color: "#ffffff",
        fontFamily: "Arial",
        backgroundColor: "#8e44ad",
        padding: { x: 6, y: 3 },
        fixedWidth: buttonWidth,
        align: "center",
      });

      button.setInteractive();
      button.on("pointerdown", () => this.toggleAnimation(animation));
      button.on("pointerover", () => button.setBackgroundColor("#9b59b6"));
      button.on("pointerout", () => {
        const isActive = this.currentAnimation === animation;
        button.setBackgroundColor(isActive ? "#e74c3c" : "#8e44ad");
      });

      this.animationButtons[animation] = button;
    });

    // Update animation button states
    this.updateAnimationButtonStates();
  }

  private toggleAnimation(animation: Animation) {
    // Update current animation
    this.currentAnimation = animation;

    // Apply animation to all cats
    this.cats.forEach((cat) => {
      cat.animate(animation, true); // true for infinite loop
    });

    // Update button states
    this.updateAnimationButtonStates();

    // Update status text
    if ((this as any).animationStatusText) {
      (this as any).animationStatusText.setText(
        `Current Animation: ${animation}`,
      );
    }
  }

  private updateAnimationButtonStates() {
    AssetLocator.getAvailableAnimations().forEach((animation) => {
      const button = this.animationButtons[animation];
      if (button) {
        const isActive = this.currentAnimation === animation;
        button.setBackgroundColor(isActive ? "#e74c3c" : "#8e44ad");
      }
    });
  }

  shutdown() {
    // Restore overlay visibility when leaving showcase
    window.dispatchEvent(
      new CustomEvent("phaser-overlay-toggle", { detail: { visible: true } }),
    );
    // Clean up cats when scene shuts down
    this.cats.forEach((cat) => cat.destroy());
    this.cats = [];
  }
}
